import { get, post } from '../../Services/ApiService.js';
import * as actionLoading from '../Loading/LoadingActions';
import * as actionToast from '../Toast/ToastActions';
import { ServerEndpoint } from '../endpoints.ts';
import CartConstants from './CartConsts';

/**
 * --- LOCAL CART MANAGEMENT ---
 */

export const addProductToCart = (product) => (dispatch) => {
    const serializable = JSON.parse(JSON.stringify(product));
    if (!serializable.quantity || serializable.quantity < 1) serializable.quantity = 1;
    dispatch({ type: CartConstants.ADD_PRODUCT_TO_CART, payload: serializable });
};

export const removeProductFromCart = (productId) => (dispatch) => {
    dispatch({ type: CartConstants.REMOVE_PRODUCT_FROM_CART, payload: productId });
};

export const updateCartProduct = (product) => (dispatch) => {
    const serializable = JSON.parse(JSON.stringify(product));
    dispatch({ type: CartConstants.EDIT_PRODUCT_IN_CART, payload: serializable });
};

export const clearCart = () => (dispatch) => {
    dispatch({ type: CartConstants.CLEAR_CART });
};


export const fetchProducts = () => async (dispatch) => {
    try {
        dispatch(actionLoading.setLoading('Syncing Inventory...'));
        const response = await get(ServerEndpoint.PRODUCTS, {}, true);
        dispatch({ type: CartConstants.FETCH_PRODUCTS_SUCCESS, payload: response.data });
    } catch (error) {
        dispatch({ type: CartConstants.FETCH_PRODUCTS_FAILURE, payload: [] });
        dispatch(actionToast.setToast('error', 'Failed to sync with Zoho Inventory'));
    } finally {
        dispatch(actionLoading.disableLoading());
    }
};

export const addProduct = (product) => async (dispatch) => {
    try {
        dispatch(actionLoading.setLoading('Registering New Item...'));
        const response = await post(ServerEndpoint.PRODUCTS_NEW, product, true);
        dispatch({ type: CartConstants.ADD_PRODUCT, payload: response.data });
        dispatch(actionToast.setToast('success', 'Product created successfully'));
        dispatch(fetchProducts());
    } catch (error) {
        dispatch(actionToast.setToast('error', 'Failed to create product'));
    } finally {
        dispatch(actionLoading.disableLoading());
    }
};

export const editProduct = (product) => async (dispatch) => {
    try {
        dispatch(actionLoading.setLoading('Updating Item Details...'));
        const response = await post(`${ServerEndpoint.PRODUCTS_EDIT}/${product.id}`, product, true);
        dispatch({ type: CartConstants.EDIT_PRODUCT, payload: response.data });
        dispatch(actionToast.setToast('success', 'Update successful'));
        dispatch(fetchProducts());
    } catch (error) {
        dispatch(actionToast.setToast('error', 'Update failed'));
    } finally {
        dispatch(actionLoading.disableLoading());
    }
};

export const removeProduct = (productId) => async (dispatch) => {
    try {
        dispatch(actionLoading.setLoading('Removing Item...'));
        await post(`${ServerEndpoint.PRODUCTS_DELETE}/${productId}`, {}, true);
        dispatch({ type: CartConstants.REMOVE_PRODUCT, payload: productId });
        dispatch(actionToast.setToast('success', 'Product removed from catalog'));
        dispatch(fetchProducts());
    } catch (error) {
        dispatch(actionToast.setToast('error', 'Delete operation failed'));
    } finally {
        dispatch(actionLoading.disableLoading());
    }
};

/**
 * --- CHECKOUT PROCESS ---
 */

export const estimateShipping = (zip, country, state, city, items, email, address) => async () => {
    try {
        const cleanedItems = items.map(({ id, quantity, sku, name, pricing, inventory, dimensions, category, specifications }) => ({
            id, quantity, sku, name, pricing, inventory, dimensions, category, specifications,
        }));
        const response = await post(ServerEndpoint.SHIPPING_ESTIMATE, {
            zip, country, state, city, items: cleanedItems,
            ...(email ? { email } : {}),
            ...(address?.line1 ? { address } : {}),
        });
        const body = response.data;
        if (!body?.status) {
            return { rates: null, error: body?.message || 'Unable to fetch shipping rates' };
        }
        return { rates: body.data || null, error: null };
    } catch {
        return { rates: null, error: 'Unable to connect to shipping service. Please try again.' };
    }
};

export const processCart = (cartItems, includeDelivery, email, userId, shippingDetails = null, shippingRate = null) => async (dispatch) => {
    try {
        dispatch(actionLoading.setLoading('Securing Checkout Session...'));

        const cleanedItems = cartItems.map(({ id, quantity }) => ({ id, quantity }));
        const response = await post(ServerEndpoint.PROCESS_CART, { cartItems: cleanedItems, includeDelivery, email, userId, shippingDetails, shippingRate }, true);
        if (response.data?.url) {
            return response.data.url;
        } else {
            dispatch(actionToast.setToast('error', 'Gateway Error: No payment URL received'));
        }
    } catch (error) {
        dispatch(actionToast.setToast('error', 'Server failed to process the cart'));
    } finally {
        dispatch(actionLoading.disableLoading());
    }
};

export const ensureGuestCustomer = (email) => async () => {
    try {
        await post(ServerEndpoint.ENSURE_CUSTOMER, { email });
    } catch {
        // fire-and-forget, non-critical
    }
};
